import { v4 as uuidv4 } from 'uuid'
import { getAuthParams } from 'fk-ui-common/utils/bridgeCaller'
import { redirectToLoginPage } from 'fk-ui-common/utils/redirectToLoginPage'
import EnvHelper from 'fk-ui-common/utils/appEnvironmentHelper'

import type { Action, SessionResponse } from 'types'
import { applicationPlatform } from 'helpers/platform'
import { trackRCBPEvent } from 'fdp-tracker/tracker'
import { USER_SESSION_KEY } from 'constants.app'

export const noop = (): void => {
  /* no operation */
}

export const isNil = (value: unknown): value is null | undefined => {
  return value === undefined || value === null || value === ''
}

export const getUrlFromAction = (action: Action): string => {
  const url = action.url ?? action.originalUrl ?? action.params.url
  if (url === undefined) {
    throw Error('URL is missing')
  }

  return url
}

export const getUrl = (
  protocol: string,
  host: string,
  pathname: string,
  search?: string | null,
): string => {
  return `${protocol}//${host}${pathname}${search ?? ''}`
}

export const getUrlWithAppendedQueryParams = (
  url: string,
  array: Array<Array<undefined | null | string | File>>,
): string => {
  const [pathname, search] = url.split('?')
  const queryParams = new URLSearchParams(search ? `?${search}` : undefined)

  for (const [key, value] of array) {
    if (typeof key === 'string' && typeof value === 'string') {
      queryParams.append(key, value)
    }
  }
  return `${pathname}?${queryParams.toString()}`
}

export const generateId = (): string => {
  return uuidv4()
}

export const toRupees = (paise: number): number => {
  return paise / 100
}
export const trimMobileNumber = (value: string | number): string | number | undefined => {
  return Number(value.toString().replace(/^(\+91|0)|\D/g, ''))
}
const REGEX_MOBILE_NUMBER =
  /^(?:(?:\+|0{0,2})91(\s*|[\\-])?|[0]?)?([6789]\d{2}([ -]?)\d{3}([ -]?)\d{4})$/

export const isValidMobileNumber = (value: string | number): boolean => {
  return REGEX_MOBILE_NUMBER.test(value.toString())
}

export const submitForm = (method: string, url: string, body: Record<string, string>): void => {
  const form = document.createElement('form')

  form.method = method
  form.action = url
  form.target = '_top'
  let element

  for (const key in body) {
    if (Object.hasOwnProperty.call(body, key)) {
      element = document.createElement('input')
      element.value = body[key]
      element.type = 'hidden'
      element.name = key
      form.appendChild(element)
    }
  }

  document.body.appendChild(form)
  form.submit()
}

export const getUserLoggedInStatus = (): string => {
  const session = getUserSessionData()
  return session && Boolean(session['isLoggedIn']) ? 'LoggedIn' : 'NotLoggedIn'
}

export const getUserSessionData = (): SessionResponse | null => {
  const sessionData = window.sessionStorage?.getItem(USER_SESSION_KEY)
  if (sessionData !== null) {
    const parsedData = JSON.parse(sessionData)
    return parsedData.SESSION
  }
  return null
}

export const removeDuplicateFromArray = <T>(list: T[]): T[] => {
  const uniq = new Set(list.map(item => JSON.stringify(item)))
  const finalFilterList = Array.from(uniq).map(item => JSON.parse(item))
  return finalFilterList
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleFDPEvent = (params: any, eventName: any, flushEvents?: boolean): any => {
  // eslint-disable-next-line new-cap
  const event = new eventName(params)
  trackRCBPEvent(event, flushEvents)
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
export const callCallbackWithResponse = (callback: string, response: any) => {
  const { status, name, number } = response
  if (callback in window && typeof window[callback] === 'function') {
    window[callback](status, name, number)
  } else {
    throw new Error(`callback: ${callback} not defined`)
  }
}

export const goToLoginPage = (): void => {
  window.location.href =
    'https://www.flipkart.com/login?ret=' + encodeURIComponent(window.location.href)
}

export const checkLogin = (): void => {
  const isLoggedIn = getUserLoggedInStatus()
  if (isLoggedIn === 'NotLoggedIn') {
    redirectToLoginPage()
  }
}

export const toPixel = (value: number | string | undefined): string => {
  return isNil(value) ? '' : value.toString() + `px`
}

export const doAppBack = (): void => {
  window.location.href = 'app://back'
}
export const handleBackNavigation = (steps = 1): void => {
  const { href } = window.location
  window.history.go(-Math.abs(steps))
  setTimeout(() => {
    if (window.location.href === href && !applicationPlatform.isMSite) {
      doAppBack()
    }
  }, 500)
}

export const getAndroidOsVersion = (): number | null => {
  let osVersion = null
  const ua = EnvHelper.getUserAgentString()
  if (ua.includes('Android')) {
    osVersion = parseFloat(ua.slice(ua.indexOf('Android') + 8))
  }
  return osVersion
}

export const getRoundOff = (amount: number, discAmount: number): number => {
  const decimalPlaces = (amount.toString().split('.')[1] || []).length
  const roundedDiscAmount = Number(discAmount.toFixed(decimalPlaces))
  return roundedDiscAmount
}

export const getSource = (searchParams: URLSearchParams): string => {
  const utmSource = searchParams.get('utm_source') ?? ''
  const utmSourceContext = searchParams.get('utm_source_context') ?? ''
  return utmSource ? `${utmSource}${utmSourceContext ? `|${utmSourceContext}` : ''}` : ''
}

export const formatConnectionNumber = (category: string | undefined, connectionNumber: string, index?: number): string => {
  return `${(index === 0 || isNil(index)) && category?.toLowerCase().includes('credit card') ? '***' : ''} ${connectionNumber}`;
};

export const formatAmount = (amount: number | string): string => {
  const numStr = amount.toString();
  const [integerPart, decimalPart] = numStr.split('.');
  if (integerPart.length <= 3) {
    return decimalPart ? integerPart + '.' + decimalPart : integerPart;
  }
  const formattedIntegerPart = integerPart.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return decimalPart ? formattedIntegerPart + ',' + integerPart.slice(-3) + '.' + decimalPart : formattedIntegerPart + ',' + integerPart.slice(-3);
}

export const getCookie = async () : Promise<string> => {
  const params = await getAuthParams();
  return params?.at;
};